/**
 * Comp `.dash` — the rail's pinned-dashboards accordion (open by default).
 * `+` does not open a builder: it asks the agent for a new dashboard, which is
 * the whole point of the simplified surface.
 */
import React, { useState } from 'react';
import { ChevronRight, LayoutGrid, Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button.js';
import { usePinnedDashboards } from '../../hooks/usePinnedDashboards.js';
import { railRowClass } from './rail-row.js';
import { moduleTag } from './simpleModuleCopy.js';

interface Props {
  activeModuleId: string;
  selectedDashboardId: string | null;
  onSelectDashboard: (dashboardId: string) => void;
  onSay: (message: string) => void;
}

export const SimpleDashboardsSection: React.FC<Props> = ({
  activeModuleId,
  selectedDashboardId,
  onSelectDashboard,
  onSay,
}) => {
  const [open, setOpen] = useState(true);
  const { data: dashboards } = usePinnedDashboards(activeModuleId);
  const showTags = activeModuleId === 'all';

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between pr-1.5">
        <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="h-auto flex-1 justify-between rounded-none px-2 pt-4 pb-1.5 font-mono text-[10px] font-normal tracking-[1.2px] text-faint uppercase hover:bg-transparent hover:text-muted"
        >
          <span>Dashboards</span>
          <ChevronRight size={14} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="New dashboard: ask the agent"
          title="New dashboard: ask the agent"
          onClick={() => onSay('Create a new dashboard for me')}
          className="text-faint hover:text-heading"
        >
          <Plus size={14} />
        </Button>
      </div>

      {open && (
        <div className="flex flex-col gap-0.5">
          {(dashboards ?? []).map((d) => {
            const id = String(d.id);
            const active = id === selectedDashboardId;
            return (
              <Button
                key={id}
                variant="ghost"
                onClick={() => onSelectDashboard(id)}
                aria-current={active ? 'true' : undefined}
                className={railRowClass({ active })}
              >
                <LayoutGrid size={12} className="shrink-0 text-accent-text" />
                <span className="overflow-hidden text-ellipsis">{d.name}</span>
                {showTags && (
                  <span className="ml-auto font-mono text-[9.5px] tracking-[0.5px] text-faint">
                    {moduleTag(d.moduleId)}
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};
